import { useState, type FormEvent } from 'react';
import type { OrphanedCard, PropertyCardInfo, SetPropertyCardInput } from '../../types/payments';
import { formatCardRef, formatInvoiceDate } from '../../lib/payments';

/* ── Property cards (CHO-771) ────────────────────────────────────────────────
 * The write-only card management section of the payments page. Built per the
 * component policy: exported, prop-driven, no internal fetching — PaymentsPage
 * loads properties, payability and card info and passes everything in.
 *
 * CHO-799: only payable properties (those the invoice payment service can
 * actually pay) get a card slot. A UI-set card stored for a non-payable
 * property is surfaced as an orphaned-card row — masked last4, source,
 * delete affordance, NO card form — never auto-deleted and never silently
 * hidden. Excluded properties with no UI-set card are omitted entirely.
 *
 * CHO-763 Rule 8 (no autocomplete): the form carries autocomplete="off" and
 * every field carries autocomplete="new-password"; no cc-* token appears
 * anywhere. This is the one file allow-listed from the no-card-input guard
 * (client/src/__tests__/no-card-input.test.ts) — every other pattern in that
 * guard still applies everywhere else, including this file's prose. The
 * orphaned-card row renders no input fields at all, so the guard's allowlist
 * needs no change.
 *
 * The raw PAN/expiry/CVV live only in these three controlled inputs while the
 * operator types. On success (savedAt changes) the fields are cleared; the
 * submitted values are never copied anywhere else.
 */

export interface PropertyCardRowProps {
  propertyId: number;
  propertyName: string;
  /** null while the card info for this property has not loaded yet. */
  card: PropertyCardInfo | null;
  /** True while a set/delete for this property is in flight. */
  busy: boolean;
  /** Form-level failure (e.g. the upstream 400) — surfaced verbatim. */
  error: string | null;
  /** Timestamp of the last successful mutation; a change clears the fields. */
  savedAt: number | null;
  onSetCard: (fields: SetPropertyCardInput) => void;
  onDeleteCard: () => void;
}

function sourceLabel(source: PropertyCardInfo['source']): string {
  if (source === 'ui') return 'Set from this page';
  if (source === 'env') return 'From the service environment';
  return 'None configured';
}

export function PropertyCardRow(props: PropertyCardRowProps) {
  // The raw values live in PropertyCardRowForm's local state only. Keying the
  // form by the last successful mutation remounts it with fresh, empty fields
  // exactly when a set/delete resolves — no effect, no retained values.
  return <PropertyCardRowForm key={props.savedAt ?? 'never-saved'} {...props} />;
}

function PropertyCardRowForm({
  propertyId,
  propertyName,
  card,
  busy,
  error,
  onSetCard,
  onDeleteCard,
}: PropertyCardRowProps) {
  const [pan, setPan] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    onSetCard({ pan, expiry, cvv });
  };

  const panId = `card-pan-${propertyId}`;
  const expiryId = `card-expiry-${propertyId}`;
  const cvvId = `card-cvv-${propertyId}`;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4" data-property-id={propertyId}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">{propertyName}</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {card ? (
              <>
                {formatCardRef(card.last4)} · {sourceLabel(card.source)}
                {card.configuredAt && <> · set {formatInvoiceDate(card.configuredAt)}</>}
              </>
            ) : (
              'Loading card…'
            )}
          </p>
        </div>
        {card?.source === 'ui' && (
          <button
            type="button"
            onClick={onDeleteCard}
            disabled={busy}
            className="px-3 py-1.5 rounded-md text-xs font-medium text-red-700 border border-red-200 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? 'Working…' : 'Delete card'}
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} autoComplete="off" className="mt-3 flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label htmlFor={panId} className="text-xs font-medium text-gray-600">
            Card number
          </label>
          <input
            id={panId}
            name="pan"
            type="text"
            inputMode="numeric"
            autoComplete="new-password"
            value={pan}
            onChange={(event) => setPan(event.target.value)}
            placeholder="12–19 digits"
            required
            disabled={busy}
            className="w-44 rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 disabled:opacity-40"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={expiryId} className="text-xs font-medium text-gray-600">
            Expiry
          </label>
          <input
            id={expiryId}
            name="expiry"
            type="text"
            inputMode="numeric"
            autoComplete="new-password"
            value={expiry}
            onChange={(event) => setExpiry(event.target.value)}
            placeholder="MM/YY"
            required
            disabled={busy}
            className="w-24 rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 disabled:opacity-40"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={cvvId} className="text-xs font-medium text-gray-600">
            CVV
          </label>
          <input
            id={cvvId}
            name="cvv"
            type="text"
            inputMode="numeric"
            autoComplete="new-password"
            value={cvv}
            onChange={(event) => setCvv(event.target.value)}
            placeholder="3–4 digits"
            required
            disabled={busy}
            className="w-20 rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 disabled:opacity-40"
          />
        </div>
        <button
          type="submit"
          disabled={busy}
          className="px-3 py-1.5 rounded-md bg-gray-900 text-white text-xs font-medium hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {busy ? 'Saving…' : card?.last4 ? 'Replace card' : 'Save card'}
        </button>
      </form>

      {error && (
        <p role="alert" className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </p>
      )}
    </div>
  );
}

export interface PropertyCardsProps {
  /** Payable properties only (CHO-799) — each gets a card slot with the form. */
  properties: Array<{ id: number; name: string }>;
  /** Latest card info per property id, as returned by the card API. */
  cards: Record<number, PropertyCardInfo>;
  /**
   * UI-set cards stored for properties the payment service can never pay.
   * Rendered as delete-only orphan rows — never auto-deleted, never hidden.
   */
  orphanedCards: OrphanedCard[];
  loading: boolean;
  /** Section-level load failure (auth-class or unreachable). */
  error: string | null;
  busyByProperty: Record<number, boolean>;
  errorByProperty: Record<number, string | null>;
  /** Last successful mutation timestamp per property — drives field clearing. */
  savedAtByProperty: Record<number, number>;
  onSetCard: (propertyId: number, fields: SetPropertyCardInput) => void;
  onDeleteCard: (propertyId: number) => void;
}

/**
 * One stored UI-set card for a property the payment service can never pay.
 * Delete affordance only — no card form, no inputs. The delete goes through
 * the existing DELETE route; its `source: "none"` response makes the row
 * disappear via the page's cards map, so the flow closes itself.
 */
function OrphanedCardRow({
  propertyName,
  card,
  busy,
  error,
  onDeleteCard,
}: OrphanedCard & { busy: boolean; error: string | null; onDeleteCard: () => void }) {
  return (
    <div
      className="bg-amber-50 border border-amber-200 rounded-lg p-4"
      data-property-id={card.propertyId}
      data-orphaned-card="true"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">{propertyName}</h3>
          <p className="text-xs text-gray-600 mt-0.5">
            {formatCardRef(card.last4)} · Set from this page
            {card.configuredAt && <> · set {formatInvoiceDate(card.configuredAt)}</>}
          </p>
          <p className="text-xs text-amber-800 mt-1">
            Stored card for a non-payable property — the payment service has no confirmed invoice account mapping
            for it, so this card can never be charged. Delete it or leave it stored; it is never used automatically.
          </p>
        </div>
        <button
          type="button"
          onClick={onDeleteCard}
          disabled={busy}
          className="px-3 py-1.5 rounded-md text-xs font-medium text-red-700 border border-red-200 bg-white hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {busy ? 'Working…' : 'Delete card'}
        </button>
      </div>

      {error && (
        <p role="alert" className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * The *Property cards* section: one row per payable property, each showing
 * the masked last four + source + when the UI card was set, with a
 * set/replace form and a delete action; plus one delete-only row per stored
 * UI card for a non-payable property. Nothing here fetches; PaymentsPage
 * owns all data.
 */
export function PropertyCards({
  properties,
  cards,
  orphanedCards,
  loading,
  error,
  busyByProperty,
  errorByProperty,
  savedAtByProperty,
  onSetCard,
  onDeleteCard,
}: PropertyCardsProps) {
  const empty = properties.length === 0 && orphanedCards.length === 0;
  return (
    <section className="mt-10" aria-label="Property cards">
      <h2 className="text-xl font-semibold text-gray-900">Property cards</h2>
      <p className="text-sm text-gray-500 mt-1">
        Cards for the properties the payment service can pay — those with a confirmed invoice account mapping to a
        known biller. Card details are write-only — once saved, only the last four digits are ever shown.
      </p>

      {error && (
        <div role="alert" className="mt-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          <p className="text-sm font-medium text-red-800">{error}</p>
        </div>
      )}

      {!error && loading && <p className="mt-4 text-sm text-gray-500">Loading properties…</p>}

      {!error && !loading && empty && (
        <p className="mt-4 text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
          No payable properties yet — a property appears here once its first invoice account mapping is confirmed.
        </p>
      )}

      {!error && !loading && !empty && (
        <ul className="mt-4 space-y-3">
          {properties.map((property) => (
            <li key={property.id}>
              <PropertyCardRow
                propertyId={property.id}
                propertyName={property.name}
                card={cards[property.id] ?? null}
                busy={!!busyByProperty[property.id]}
                error={errorByProperty[property.id] ?? null}
                savedAt={savedAtByProperty[property.id] ?? null}
                onSetCard={(fields) => onSetCard(property.id, fields)}
                onDeleteCard={() => onDeleteCard(property.id)}
              />
            </li>
          ))}
          {orphanedCards.map((orphan) => (
            <li key={orphan.propertyId}>
              <OrphanedCardRow
                {...orphan}
                busy={!!busyByProperty[orphan.propertyId]}
                error={errorByProperty[orphan.propertyId] ?? null}
                onDeleteCard={() => onDeleteCard(orphan.propertyId)}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
